import { useEffect } from 'react'
import plus from 'styleguide/icons/ds2/plus-white.svg'
import RequestsTabs from './RequestsTabs'
import TimeOffBalance from './TimeOffBalance'
import { EmployeeInfo } from './EmployeeInfo'
import { useNavigate, useParams } from 'react-router-dom'
import { useProfileDisplay } from '../../hooks/useProfileDisplay'
import ValidationBanner from '../../components/common/ValidationBanner'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../store/hooks'
import { RootState } from '../../store/store'
import ManualTimeOffBanner from '../../components/common/ManualTimeOffBanner'
import { clearSelectedEmployee } from '../../store/slices/admin/manualTimeOffSlice'
import { fetchManualTimeOffProfile } from '../../store/slices/admin/manualTimeOffProfileSlice'
import Loader from '../../components/common/Loader'
import {
  clearEmployeeScopedData,
  getEmployeeInfo,
} from '../../store/slices/Requestor/employeeInfoSlice'

const NoApproverBanner = () => (
  <ValidationBanner
    variant="error"
    heading="No Approver on Record"
    className="w-[75%]!"
    message={
      <>
        <p>
          You are currently not associated with a primary approver. When the
          employee's Authorized Approver displays "NA – No Approver on Record,"
          the employee cannot access TORT.
        </p>
        <br />
        <ol className="list-decimal list-outside pl-6!">
          <li>
            Employees returning from Leave of Absence (LOA), FMLA, intermittent
            leave, or modified work schedules may be unable to access TORT. Have
            the employee's manager contact the National Human Resources Service
            Center (NHRSC) at 877-457-4772 to verify the 1510 Return from Leave
            form was processed. TORT access is restored within 7–14 business
            days after the form is processed.
          </li>
          <br />
          <li>
            Employees with an inactive KP TIME Primary Approver may be unable to
            access TORT. If your KP TIME Primary Approver is no longer at KP,
            transferred, retired, or terminated, the team cannot utilize TORT.
            Please have a manager complete the TIME Approval Group form 3645 and
            follow up with{' '}
            <a href="mailto:NPS-National-Timekeeping@kp.org">
              NPS-National-Timekeeping@kp.org
            </a>{' '}
            for the status. TORT access is restored within 3-7 business days
            after the form is processed.
          </li>
          <br />
          <li>
            Employees that recently transferred to a different department may
            not be able to access TORT. Please have a manager complete the TIME
            employee Add/Change form 3646 and follow up with{' '}
            <a href="mailto:NPS-National-Timekeeping@kp.org">
              NPS-National-Timekeeping@kp.org
            </a>{' '}
            for the status. TORT access is restored within 3-7 business days
            after the form is processed.
          </li>
        </ol>
        <br />
        <p>
          If these scenarios do not apply to you, please contact the National
          TORT Support team at{' '}
          <a href="mailto:NAT-TORT@kp.org">NAT-TORT@kp.org</a>. Please include
          the employee's full name, NUID, employee ID, description of your
          issue, and appropriate computer screenshots. This will help us
          research the issue and provide support. Thank you.
        </p>
      </>
    }
  />
)

const RequestsContainer = () => {
  const navigate = useNavigate()
  const { mode } = useParams<{ mode?: string }>()
  const dispatch = useAppDispatch()

  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()

  const profileData = useSelector((state: RootState) => state.profile.data)
  const profileError = useSelector((state: RootState) => state.profile.error)

  const manualProfile = useSelector(
    (state: RootState) => state.manualTimeOffProfile
  )

  const activeTab = useSelector(
    (state: RootState) => state.requestorTab.activeTab
  )

  const manualSelected = useSelector(
    (state: RootState) => state.manualTimeOff?.selectedEmployee
  )

  const employeeInfoError = useSelector(
    (state: RootState) => state.employeeInfo?.employeeInfo?.error
  )

  const isAdminMode = mode === 'admin'

  const hasValidApprover = profileData?.roles?.hasAuthorizedApprover

  const profileInitialized = useSelector((state: RootState) => state.profile.initialized)
  const profileLoading = useSelector((state: RootState) => state.profile.loading)
  const showNoApproverBanner = (() => {
    if (isAdminMode) {
      return !!manualProfile?.initialized && !manualProfile?.error && manualProfile?.data?.roles?.hasAuthorizedApprover === false
    }
    return !!profileInitialized && !profileError && hasValidApprover === false
  })()

  const isProfileError = Boolean(profileError) || (isAdminMode && Boolean(manualProfile?.error))
  const employeeInfoFailed = Boolean(employeeInfoError) || isProfileError

  const headerContent = (() => {
    if (profileLoading && !profileInitialized) {
      return <Loader />
    }

    if (showNoApproverBanner) {
      return <NoApproverBanner />
    }

    return (
      <>
        <p className="p3 -book mb-0!">You are logged in as:</p>

        <h3 className="h3 mb-0! mt-0!">
          {userInfo} {userNuid}
        </h3>

        <p className="p3 -book mt-0! desktop:mb-2!">
          Primary Approver: {primaryApproverInfo}
        </p>
      </>
    )
  })()

  useEffect(() => {
    const promise = dispatch(getEmployeeInfo())

    return () => {
      promise?.abort?.()
    }
  }, [dispatch, manualSelected?.nuid])

  useEffect(() => {
    if (isAdminMode) {
      dispatch(fetchManualTimeOffProfile())
    }
  }, [isAdminMode, dispatch, manualSelected?.nuid])

  const handleCreate = () => {
    const basePath = mode ? `/${mode}/requestor` : '/requestor'
    navigate(`${basePath}/create`)
  }

  const handleSelectEmployee = () => {
    dispatch(clearSelectedEmployee())
    dispatch(clearEmployeeScopedData())
    navigate('/admin/manual-timeoff')
  }

  if (employeeInfoFailed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-200 p-4">
        <div className="w-full max-w-5xl">
          <ValidationBanner
            variant="error"
            className="!rounded-[8px] !border !border-red-600 !bg-white"
            heading={
              <p className="p3 -medium m-0! leading-[1.4]! desktop:whitespace-nowrap">
                System error: Log out of TORT completely, then sign back in.
              </p>
            }
            message={
              <p
                className="p3 -book m-0!"
                style={{
                  color: 'var(--Inky, var(--Color-Inky, #0D1C3D))',
                  fontSize:
                    'var(--Desktop-P3-Book-font-size, 16px)',
                  fontStyle: 'normal',
                  fontWeight: 325,
                  lineHeight:
                    'var(--Desktop-P3-Book-line-height, 24px)',
                  letterSpacing:
                    'var(--Desktop-P3-Book-letter-spacing, 0)',
                }}
              >
                The system is experiencing intermittent issues. If the problem
                continues after re-logging in, email the National TORT Support
                team at{' '}
                <a
                  href="mailto:NAT-TORT@kp.org"
                  className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
                >
                  NAT-TORT@kp.org
                </a>
                {' '}
                with your name, employee ID, NUID, computer screenshots, and a
                description of the issue.
              </p>
            }
            compact={false}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="page-container min-h-[70vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <div className="padding-bottom-2u margin-top-4u desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-3.5!">
        {isAdminMode ? (
          <>
            <h1 className="h1">Time Off</h1>
            <p className="p3 -book mb-2">
              Request time off or manage your requests
            </p>
            <ManualTimeOffBanner />
            {manualProfile?.loading && !manualProfile?.initialized ? (
              <Loader />
            ) : (
              showNoApproverBanner && <NoApproverBanner />
            )}
          </>
        ) : (
          <>
            <h1 className="h1">Time Off Request Tracker (TORT)</h1>

            {headerContent}
          </>
        )}

        {!showNoApproverBanner && activeTab !== 3 && (
          <div className="flex flex-col w-fit">
            <button
              type="button"
              className="button -light max-w-none! mt-8!"
              data-analytics-type="button"
              data-analytics-click="Default"
              onClick={handleCreate}
            >
              <img src={plus} alt="Plus Icon" className="inline-block mr-2" />
              {isAdminMode
                ? 'Create New Time Off Request for this Employee'
                : 'Create a New Request'}
            </button>

            {isAdminMode && (
              <button
                type="button"
                className="button -light max-w-none! mt-8!"
                data-analytics-type="button"
                data-analytics-click="Default"
                onClick={handleSelectEmployee}
              >
                Select Another Employee
              </button>
            )}
          </div>
        )}
      </div>

      {!showNoApproverBanner && (
        <>
          <RequestsTabs />
          <p className="p3 -book">
            (P) Request contains one or more Partial Days.
          </p>
        </>
      )}

      <TimeOffBalance />
      <EmployeeInfo skipFetch />
    </div>
  )
}

export default RequestsContainer
